import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuditStore } from '@/hooks/useAuditStore';
import { ROUTE_PATHS, AUDIT_CATEGORIES } from '@/lib/index';

export function ContinueAuditBanner() {
  const navigate = useNavigate();
  const { companyProfile, answers, result, getProgress } = useAuditStore();
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Zeige Banner nur wenn:
    // 1. Unternehmensprofil ausgefüllt
    // 2. Mindestens eine Antwort gegeben
    // 3. Audit nicht abgeschlossen
    // 4. Nicht dismissed
    // 5. Nicht auf Audit-Seite
    const isOnAuditPage = window.location.hash.includes('/audit');
    const hasStarted = companyProfile && Object.keys(answers).length > 0;
    const isIncomplete = !result;
    const shouldShow = hasStarted && isIncomplete && !dismissed && !isOnAuditPage;
    
    setShowBanner(shouldShow);
  }, [companyProfile, answers, result, dismissed]);

  const handleContinue = () => {
    navigate(ROUTE_PATHS.AUDIT);
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowBanner(false);
  };

  const progress = getProgress();
  const answeredCount = Object.keys(answers).length;
  const totalQuestions = 100;

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed top-20 left-1/2 transform -translate-x-1/2 z-40 w-full max-w-2xl px-4"
        >
          <Card className="shadow-2xl border-2 border-primary/20 bg-card/95 backdrop-blur-sm">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Audit fortsetzen</CardTitle>
                    <CardDescription>
                      Sie haben ein unvollständiges Audit
                    </CardDescription>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDismiss}
                  className="h-8 w-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Fortschritt</span>
                  <span className="font-medium">
                    {answeredCount} von {totalQuestions} Fragen ({progress.toFixed(1)}%)
                  </span>
                </div>
                <div className="w-full bg-secondary rounded-full h-2">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.5 }}
                    className="bg-primary h-2 rounded-full"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button onClick={handleContinue} className="flex-1">
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Jetzt fortsetzen
                </Button>
                <Button onClick={handleDismiss} variant="outline">
                  Später
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                Ihre Antworten wurden automatisch gespeichert
              </p>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
